import React, { FC, useMemo } from 'react';

import { EvFlexColumn } from '@evinced-private/ui-common';

import { WFA_DASHBOARD_CONTAINER_DAID } from 'data-automation-ids/WfaDashboardAutomationIds';
import { AccessibilitySummaryContainer } from 'src/apps/dashboard/components/accessibility-summary/AccessibilitySummaryContainer';
import { BaseFilters } from 'src/apps/dashboard/components/base-filters/BaseFilters';
import { DASHBOARD_DATE_RANGE_OPTIONS } from 'src/apps/dashboard/constants/DashboardFiltersDefinitions';
import { DashboardTemplateProps } from 'src/apps/dashboard/types/DashboardTemplate.types';

import { DashboardTemplateCharts } from './dashboard-template-charts';
import { DashboardTemplateHeader } from './dashboard-template-header';
import { DashboardTemplateBottom } from './DashboardTemplateBottom';
import { DASHBOARD_TEMPLATE_FILTER_PRESETS } from './DashboardTemplateFilterPresets.constants';

import 'src/apps/dashboard/components/dashboard-filters/DashboardFilters.scss';
import 'src/apps/dashboard/pages/issues/components/IssuesFilters.scss';

export const DashboardTemplate: FC<DashboardTemplateProps> = ({
	header,
	filter,
	effectiveFilters,
	scoreQuery,
	dashboardQuery,
	summaryDashboardQuery = dashboardQuery,
	isUniquenessServiceEnabled,
	chartVariant = 'flat',
	showCharts = true,
	containerAutomationId = WFA_DASHBOARD_CONTAINER_DAID,
	bottom,
	totalIssuesIsUnique
}) => {
	const scoreDataList = scoreQuery.data?.scores ?? [];
	const {
		filterPreset = 'dashboard',
		filterOptions: customFilterOptions,
		filters,
		filterDefinitions,
		onFilterChange
	} = filter;
	const preset = DASHBOARD_TEMPLATE_FILTER_PRESETS[filterPreset];
	const isIssuesPage = filterPreset === 'issues';

	const filterOptions = useMemo(() => {
		if (customFilterOptions) {
			return customFilterOptions;
		}

		if (filterPreset === 'dashboard') {
			return {
				dateRange: DASHBOARD_DATE_RANGE_OPTIONS
			};
		}

		return customFilterOptions ?? {};
	}, [customFilterOptions, filterPreset]);

	return (
		<EvFlexColumn gap="XLARGE" dataAutomationId={containerAutomationId}>
			<EvFlexColumn gap="XLARGE">
				<DashboardTemplateHeader
					header={header}
					dashboardLastUpdatedAt={dashboardQuery.data?.lastUpdatedAt ?? 0}
					scoreLastUpdatedAt={scoreQuery.data?.lastUpdatedAt ?? 0}
				/>
				<BaseFilters
					filters={filters}
					onFilterChange={onFilterChange}
					filterDefinitions={filterDefinitions}
					filterOptions={filterOptions}
					ariaLabel={preset.ariaLabel}
					skipLinkId={preset.skipLinkId}
					dataAutomationId={preset.dataAutomationId}
					hasTextFilter={preset.hasTextFilter}
					forceRemountOnFilterChange={preset.forceRemountOnFilterChange}
				/>
			</EvFlexColumn>

			<EvFlexColumn gap="XLARGE">
				{!isIssuesPage && (
					<EvFlexColumn role="group" ariaLabelledby="accessibility-summary-heading" as="section">
						<AccessibilitySummaryContainer
							scoreData={scoreDataList}
							uniqueIssuesData={summaryDashboardQuery.data?.uniqueIssues}
							totalIssuesData={summaryDashboardQuery.data?.totalIssues}
							filters={effectiveFilters}
							isLoading={summaryDashboardQuery.isLoading || scoreQuery.isLoading}
							error={summaryDashboardQuery.error || scoreQuery.error}
							isUniquenessServiceEnabled={isUniquenessServiceEnabled}
							totalIssuesIsUnique={totalIssuesIsUnique}
						/>
					</EvFlexColumn>
				)}

				{showCharts && !isIssuesPage && (
					<DashboardTemplateCharts
						scoreQuery={scoreQuery}
						dashboardQuery={dashboardQuery}
						variant={chartVariant}
					/>
				)}

				<DashboardTemplateBottom
					bottom={bottom}
					isUniquenessServiceEnabled={isUniquenessServiceEnabled}
				/>
			</EvFlexColumn>
		</EvFlexColumn>
	);
};
